import * as React from 'react';

interface CustomProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  onChange: (event: { target: { name: string; value: number } }) => void;
  name: string;
  value?: number;
  // como o número é exibido (padrão: valor monetário sem o símbolo)
  format?: (value?: number) => string;
}

function onlyDigits(value: string): string {
  return value.replace(/\D/g, '');
}

function normalizeDigits(value: string): string {
  const normalized = value.replace(/^0+(?=\d)/, '');
  return normalized || '0';
}

function valueToDigits(value?: number): string {
  return normalizeDigits(String(Math.round((value || 0) * 100)));
}

function digitsToNumber(digits: string): number {
  return Number(normalizeDigits(digits)) / 100;
}

function formatMoney(value?: number): string {
  return (value || 0)
    .toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .replace('R$', '');
}

function getDigitSelection(input: HTMLInputElement) {
  const selectionStart = input.selectionStart ?? input.value.length;
  const selectionEnd = input.selectionEnd ?? input.value.length;

  return {
    start: onlyDigits(input.value.slice(0, selectionStart)).length,
    end: onlyDigits(input.value.slice(0, selectionEnd)).length,
  };
}

function getCaretPositionByDigitIndex(value: string, digitIndex: number): number {
  if (digitIndex <= 0) {
    const firstDigitIndex = value.search(/\d/);
    return firstDigitIndex >= 0 ? firstDigitIndex : value.length;
  }

  let digits = 0;

  for (let index = 0; index < value.length; index++) {
    if (!/\d/.test(value[index])) continue;

    digits++;
    if (digits === digitIndex) return index + 1;
  }

  return value.length;
}

const MoneyInput = React.forwardRef<HTMLInputElement, CustomProps>(function MoneyInput(props, ref) {
  const { onChange, value, name, onKeyDown, onPaste, format = formatMoney, ...other } = props;
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  function setRefs(node: HTMLInputElement | null) {
    inputRef.current = node;

    if (typeof ref === 'function') {
      ref(node);
      return;
    }

    if (ref) {
      ref.current = node;
    }
  }

  function moveCaret(digitIndex?: number) {
    window.requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) return;

      const caretPosition =
        digitIndex === undefined ? input.value.length : getCaretPositionByDigitIndex(input.value, digitIndex);

      input.setSelectionRange(caretPosition, caretPosition);
    });
  }

  function emit(nextDigits: string, nextDigitIndex?: number) {
    onChange({ target: { name, value: digitsToNumber(nextDigits) } });
    moveCaret(nextDigitIndex);
  }

  function replaceSelection(input: HTMLInputElement, replacement: string) {
    const digits = valueToDigits(value);
    const selection = getDigitSelection(input);
    const nextDigits = normalizeDigits(
      `${digits.slice(0, selection.start)}${replacement}${digits.slice(selection.end)}`,
    );

    return {
      nextDigits,
      nextDigitIndex: selection.start + replacement.length,
    };
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    onKeyDown?.(event);
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;

    if (/^\d$/.test(event.key)) {
      event.preventDefault();
      const input = event.currentTarget;
      const selection = getDigitSelection(input);
      const hasSelection = input.selectionStart !== input.selectionEnd;
      const isAtEnd = selection.start === valueToDigits(value).length && selection.end === valueToDigits(value).length;

      if (hasSelection) {
        const { nextDigits, nextDigitIndex } = replaceSelection(input, event.key);
        emit(nextDigits, nextDigitIndex);
        return;
      }

      if (isAtEnd) {
        emit(`${valueToDigits(value)}${event.key}`);
        return;
      }

      const nextDigits = normalizeDigits(
        `${valueToDigits(value).slice(0, selection.start)}${event.key}${valueToDigits(value).slice(selection.start)}`,
      );

      emit(nextDigits, selection.start + 1);
      return;
    }

    if (event.key === 'Backspace') {
      event.preventDefault();
      const input = event.currentTarget;
      const hasSelection = input.selectionStart !== input.selectionEnd;
      const digits = valueToDigits(value);

      if (hasSelection) {
        const { nextDigits, nextDigitIndex } = replaceSelection(input, '');
        emit(nextDigits, nextDigitIndex);
        return;
      }

      const selection = getDigitSelection(input);
      const isAtEnd = selection.start === digits.length;

      if (isAtEnd) {
        emit(digits.slice(0, -1));
        return;
      }

      const removeIndex = Math.max(selection.start - 1, 0);
      const nextDigits = normalizeDigits(`${digits.slice(0, removeIndex)}${digits.slice(selection.start)}`);

      emit(nextDigits, removeIndex);
      return;
    }

    if (event.key === 'Delete') {
      event.preventDefault();
      const input = event.currentTarget;
      const hasSelection = input.selectionStart !== input.selectionEnd;

      if (hasSelection) {
        const { nextDigits, nextDigitIndex } = replaceSelection(input, '');
        emit(nextDigits, nextDigitIndex);
        return;
      }

      const digits = valueToDigits(value);
      const selection = getDigitSelection(input);
      const nextDigits = normalizeDigits(`${digits.slice(0, selection.start)}${digits.slice(selection.start + 1)}`);

      emit(nextDigits, selection.start);
    }
  }

  function handlePaste(event: React.ClipboardEvent<HTMLInputElement>) {
    onPaste?.(event);
    if (event.defaultPrevented) return;

    const pastedDigits = onlyDigits(event.clipboardData.getData('text'));
    if (!pastedDigits) return;

    event.preventDefault();

    const input = event.currentTarget;
    const selection = getDigitSelection(input);
    const hasSelection = input.selectionStart !== input.selectionEnd;
    const isAtEnd = selection.start === valueToDigits(value).length && selection.end === valueToDigits(value).length;

    if (hasSelection) {
      const { nextDigits, nextDigitIndex } = replaceSelection(input, pastedDigits);
      emit(nextDigits, nextDigitIndex);
      return;
    }

    if (isAtEnd) {
      emit(`${valueToDigits(value)}${pastedDigits}`);
      return;
    }

    const nextDigits = normalizeDigits(
      `${valueToDigits(value).slice(0, selection.start)}${pastedDigits}${valueToDigits(value).slice(selection.start)}`,
    );

    emit(nextDigits, selection.start + pastedDigits.length);
  }

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    emit(onlyDigits(event.currentTarget.value));
  }

  return (
    <input
      {...other}
      ref={setRefs}
      value={format(value)}
      inputMode="numeric"
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onChange={handleChange}
    />
  );
});

export default MoneyInput;
